import { ICriteria } from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import {
   IAudience,
   IMetric,
   TCampaignKind,
   TLeadType,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { PERIOD_DAYS } from '@forwardflow/features/campaign-flow/utils/format'
import { hasFilterValue } from '@forwardflow/features/campaign-filter/utils/filter-values'

/**
 * Turning what the existing backend returns into exactly what the screens
 * show. Pure functions, covered by live-derive.test.ts.
 */

/* ---------- Lead counts (POST /active-lead-sources/estimated-leads) ---------- */

/** The line between a new and a cold lead: under 30 days old is new, older is cold. */
export const NEW_LEAD_DAYS = 30
/** "Recent activity" = Follow Up Boss activity in the last two weeks. */
export const ACTIVITY_DAYS = 14

const daysAgoIso = (days: number, now: Date) =>
   new Date(now.getTime() - days * 864e5).toISOString()

/**
 * Leads the dialer can actually reach; also keeps the criteria list
 * non-empty (an empty list is a server error). `has_valid_phone` is not in
 * the count endpoint's field list (400 "unsupported field", verified on
 * production 2026-10-28); a present phone is.
 */
const REACHABLE: ICriteria = {
   field: 'phone',
   operator: 'IS NOT NULL',
   logic: 'AND',
}

const newAt = (days: number): ICriteria => ({
   field: 'age',
   operator: '<=',
   value: days,
   logic: 'AND',
})
const coldAt = (days: number): ICriteria => ({
   field: 'age',
   operator: '>',
   value: days,
   logic: 'AND',
})
const active = (now: Date): ICriteria => ({
   field: 'last_activity_date',
   operator: '>=',
   value: daysAgoIso(ACTIVITY_DAYS, now),
   logic: 'AND',
})

/** AI-classified interest on each lead: BUYER, SELLER or BOTH. Renters are not classified yet. */
const INTEREST: Record<TLeadType, string[]> = {
   buyer: ['BUYER', 'BOTH'],
   seller: ['SELLER', 'BOTH'],
   rental: [],
}

/**
 * The lead-type part of a count. All three types = no filter (unclassified
 * leads included, as the campaign does). Only types nobody is classified as
 * = 'none' (the count is 0).
 */
export const leadTypeCriterion = (
   types: TLeadType[]
): ICriteria | null | 'none' => {
   if (!types.length || types.length === 3) return null
   const values = [...new Set(types.flatMap((t) => INTEREST[t]))]
   if (!values.length) return 'none'
   return {
      field: 'lead_interest_type',
      operator: 'IN',
      value: values,
      logic: 'AND',
   }
}

/** One filter row as the count endpoint reads it (ponds are numeric ids). */
export const rowCriterion = (
   f: IAudience['filters'][number]
): ICriteria | null =>
   hasFilterValue(f)
      ? {
           field: f.field,
           operator: f.operator,
           value:
              f.field === 'assigned_pond_id' && Array.isArray(f.value)
                 ? f.value.map((v) => Number(v))
                 : f.value,
           logic: 'AND',
        }
      : null

/**
 * The backend joins criteria with AND and no brackets, so "new OR recently
 * active" cannot be one query. The selected kinds are split into parts that
 * do not overlap; their counts add up to the union.
 */
export const kindParts = (
   kinds: TCampaignKind[],
   now = new Date(),
   ageDays = NEW_LEAD_DAYS
): ICriteria[][] => {
   const has = (k: TCampaignKind) => kinds.includes(k)
   const n = has('new-lead')
   const c = has('revive')
   const a = has('activity')
   const NEW = newAt(ageDays)
   const COLD = coldAt(ageDays)
   if (n && c) return [[]]
   if (n && a) return [[NEW], [COLD, active(now)]]
   if (c && a) return [[COLD], [NEW, active(now)]]
   if (n) return [[NEW]]
   if (c) return [[COLD]]
   if (a) return [[active(now)]]
   return []
}

/** Every query whose counts sum to the audience size. Empty = nobody matches. */
export const countQueries = (
   audience: Pick<IAudience, 'kinds' | 'leadTypes' | 'filters' | 'ageDays'>,
   now = new Date()
): ICriteria[][] => {
   const type = leadTypeCriterion(audience.leadTypes)
   if (type === 'none') return []
   const rows = audience.filters
      .map(rowCriterion)
      .filter((c): c is ICriteria => !!c)
   return kindParts(
      audience.kinds,
      now,
      audience.ageDays ?? NEW_LEAD_DAYS
   ).map((part) => [
      REACHABLE,
      ...part,
      ...(type ? [type] : []),
      ...rows,
   ])
}

/**
 * The kinds as v2 audience criteria, where they can be one AND-ed rule
 * (new only, cold only). Mixed kinds = no age rule.
 */
export const kindCriteria = (
   kinds: TCampaignKind[],
   now = new Date(),
   ageDays = NEW_LEAD_DAYS
): ICriteria[] => {
   /*
    * The v2 engine takes created_at, not the count endpoint's age-in-days
    * (handoff section 7): new = created in the last ageDays (the person's
    * own line when they said one, 30 days otherwise), cold = before.
    */
   const line = new Date(now.getTime() - ageDays * 864e5).toISOString()
   return kinds.length === 1 && kinds[0] === 'new-lead'
      ? [{ field: 'created_at', operator: '>=', value: line, logic: 'AND' }]
      : kinds.length === 1 && kinds[0] === 'revive'
        ? [{ field: 'created_at', operator: '<', value: line, logic: 'AND' }]
        : []
}

/* ---------- Results over time (GET /campaigns/stats, daily series) ---------- */

export type TDayRow = { date: string } & Record<string, number | string>

const isoDay = (d: Date) => d.toISOString().slice(0, 10)

/** The current window (N days ending today, UTC) and the N days before it. */
export const periodWindows = (period: TPeriod, now = new Date()) => {
   const n = PERIOD_DAYS[period]
   const day = (back: number) => isoDay(new Date(now.getTime() - back * 864e5))
   /* Lifetime has no period before it to compare with. */
   const lifetime = period === 'all'
   return {
      days: n,
      lifetime,
      from: lifetime ? day(n - 1) : day(2 * n - 1),
      currentFrom: day(n - 1),
      previousTo: day(n),
      to: day(0),
   }
}

export const num = (v: unknown) => Number(v) || 0

const inRange = (date: string, from: string, to: string) =>
   date >= from && date <= to

export const sumRange = (
   series: TDayRow[],
   from: string,
   to: string,
   pick: (r: TDayRow) => number
) =>
   series
      .filter((r) => inRange(String(r.date), from, to))
      .reduce((s, r) => s + pick(r), 0)

/** 12 points across the window, oldest first; empty days count as 0. */
export const buckets = (
   series: TDayRow[],
   from: string,
   days: number,
   pick: (r: TDayRow) => number,
   points = 12
): number[] => {
   const start = Date.parse(`${from}T00:00:00Z`)
   const out = Array(points).fill(0) as number[]
   for (const r of series) {
      const i = Math.floor((Date.parse(`${r.date}T00:00:00Z`) - start) / 864e5)
      if (i < 0 || i >= days) continue
      out[Math.min(points - 1, Math.floor((i * points) / days))] += pick(r)
   }
   return out
}

/** A metric for the tiles: this window, the window before, and its 12-point line. */
export const windowMetric = (
   series: TDayRow[],
   w: ReturnType<typeof periodWindows>,
   pick: (r: TDayRow) => number
): IMetric => ({
   value: sumRange(series, w.currentFrom, w.to, pick),
   previous: w.lifetime ? 0 : sumRange(series, w.from, w.previousTo, pick),
   trend: buckets(series, w.currentFrom, w.days, pick),
})

/** A ratio metric (e.g. touches per lead) built from two summed metrics. */
export const ratioMetric = (
   series: TDayRow[],
   w: ReturnType<typeof periodWindows>,
   top: (r: TDayRow) => number,
   bottom: (r: TDayRow) => number
): IMetric => {
   const ratio = (a: number, b: number) => (b ? a / b : 0)
   const t = buckets(series, w.currentFrom, w.days, top)
   const b = buckets(series, w.currentFrom, w.days, bottom)
   return {
      value: ratio(
         sumRange(series, w.currentFrom, w.to, top),
         sumRange(series, w.currentFrom, w.to, bottom)
      ),
      previous: ratio(
         sumRange(series, w.from, w.previousTo, top),
         sumRange(series, w.from, w.previousTo, bottom)
      ),
      trend: t.map((v, i) => ratio(v, b[i])),
   }
}

/* What each tile means, in the backend's daily columns. */
export const PICK = {
   /** "Leads worked": leads we called, texted or emailed. */
   leads: (r: TDayRow) => num(r.leads_contacted),
   /** "Conversations": calls that became a conversation plus leads who texted back. */
   conversations: (r: TDayRow) =>
      num(r.calls_conversations) + num(r.leads_replied),
   appointments: (r: TDayRow) => num(r.appointments_ff),
   /** Live transfers that connected: the team member and the lead both spoke. */
   transfers: (r: TDayRow) => num(r.handoffs_success),
   touches: (r: TDayRow) => num(r.calls_total) + num(r.sms_sent),
   active: (r: TDayRow) =>
      num(r.calls_total) + num(r.sms_sent) + num(r.emails_sent),
}

/** The most recent day with any outreach, as "12 min ago" style text. */
export const lastActive = (series: TDayRow[], now = new Date()) => {
   const days = series
      .filter((r) => PICK.active(r) > 0)
      .map((r) => String(r.date))
      .sort()
   const last = days[days.length - 1]
   if (!last) return null
   const diff = Math.round(
      (Date.parse(`${isoDay(now)}T00:00:00Z`) -
         Date.parse(`${last}T00:00:00Z`)) /
         864e5
   )
   return diff <= 0 ? 'Today' : diff === 1 ? 'Yesterday' : `${diff} days ago`
}

/* ---------- Timing (cadence localTime from the calling heatmap) ---------- */

/** The best UTC hour from best-time-to-call, as HH:mm in this browser's time zone. */
export const localHour = (utcHour: number, now = new Date()) => {
   const d = new Date(
      Date.UTC(
         now.getUTCFullYear(),
         now.getUTCMonth(),
         now.getUTCDate(),
         utcHour
      )
   )
   return `${String(d.getHours()).padStart(2, '0')}:00`
}

/** Calls at the best hour, texts half an hour after. */
export const timeFor = (action: 'call' | 'sms' | 'email', best: string) =>
   action === 'call' ? best : `${best.slice(0, 2)}:30`
